import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LatLngLiteral } from "leaflet";
import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { MapPinned } from "lucide-react";
import { Select } from "antd";
import { CreateEventFormValues } from "../types";
import { ILocation } from "../../Location/types";
import { GeofenceMapContainer } from "components/Map/GeofenceMapContainer";
import { LocationTypeSelector } from "../../OceanWeatherIntelligence/OperationRuleLocation/Components/LocationTypeSelector";
import { BackButton } from "components/Map/BackButton";
import { useStores } from "stores/setup/use-store";
import { MapPointPreview } from "components/Map/MapPointPreview";
import { LocationDataWarningModal } from "pages/OceanWeatherIntelligence/InsightAlert/Components/LocationDataWarningModal";
import { MapDrawOptions } from "pages/Alert/components/MapDrawOptions";

type LocationType = "point" | "area";

interface EventStep1BasicInfoProps {
  values: CreateEventFormValues;
  errors: any;
  touched: any;
  handleChange: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  selectedGeofence: LatLngLiteral[];
  handleDrawingMode: () => void;
  handlePointSelectionMode?: () => void;
  handleGeoJsonLoaded: (geojson: any) => void;
  t: (key: string) => string;
  showGeofenceError?: boolean;
  clearGeofence: () => void;
  clearSelectedStation?: () => void;
  setIsGeofenceLoadedByGeoJson: (value: boolean) => void;
  areas: ILocation[];
  handleAreaSelection?: (
    selectedAreaId: number,
    areas: ILocation[],
    setFieldValue: (field: string, value: any) => void,
  ) => void;
  handleEnableAreaSelector?: (value: boolean) => void;
  areaSelectorVisible?: boolean;
  isLocationLocked?: boolean;
}

/**
 * EventStep1BasicInfo - Step 1 for Event creation
 *
 * Simplified version of Step1BasicInfo without the category tabs.
 * Only shows the "Localidade monitorada" (monitored location) component.
 *
 * This component is used in the Event creation drawer where we don't
 * need the "Meu próprio alerta" vs "Vincular a um insight" tabs.
 */
const EventStep1BasicInfo: React.FC<EventStep1BasicInfoProps> = ({
  values,
  errors,
  touched,
  handleChange,
  selectedGeofence,
  handleDrawingMode,
  handlePointSelectionMode,
  handleGeoJsonLoaded,
  setFieldValue,
  t,
  showGeofenceError,
  clearGeofence,
  clearSelectedStation,
  setIsGeofenceLoadedByGeoJson,
  areas,
  handleAreaSelection,
  handleEnableAreaSelector,
  areaSelectorVisible,
  isLocationLocked = false,
}) => {
  const {
    dataStores: { insightAlertStore },
  } = useStores();

  // Read directly from store to ensure reactivity when station is saved
  const selectedStationForAlert = insightAlertStore.selectedStationForAlert;

  // State for location data warning modal
  const [isLocationWarningModalOpen, setIsLocationWarningModalOpen] =
    useState(false);
  const [pendingBackAction, setPendingBackAction] =
    useState<(() => void) | null>(null);

  useEffect(() => {
    if (insightAlertStore.alertLocationType) {
      return;
    }

    const hasGeofence = values.geofence && values.geofence.length > 0;
    const hasLocationId = !!values.locationId;
    const hasStationRegion = !!values.stationRegion;
    const hasSelectedStation = !!selectedStationForAlert;

    if (hasGeofence || hasLocationId) {
      insightAlertStore.setAlertLocationType("area");
    } else if (hasStationRegion || hasSelectedStation) {
      insightAlertStore.setAlertLocationType("point");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selected station to form values
  useEffect(() => {
    if (selectedStationForAlert) {
      const stationRegion = `${selectedStationForAlert.station_id}-${selectedStationForAlert.region_id}`;
      setFieldValue("stationRegion", stationRegion);
      setFieldValue("macroRegionId", selectedStationForAlert.region_id);
      // Set area name for display in preview modal, but only if not already set
      if (!values.area) {
        const areaName =
          selectedStationForAlert.macro_region_name ||
          selectedStationForAlert.station_name ||
          t("monitoredLocation");
        setFieldValue("area", areaName);
      }
    }
  }, [selectedStationForAlert, setFieldValue, t, values.area]);

  useEffect(() => {
    if (selectedGeofence && selectedGeofence.length > 0) {
      const currentGeofence = values.geofence || [];
      if (
        JSON.stringify(currentGeofence) !== JSON.stringify(selectedGeofence)
      ) {
        setFieldValue("geofence", selectedGeofence);
        // Set area name for display in preview modal (only if not already set by area selection)
        if (!values.area) {
          setFieldValue("area", t("monitoredArea"));
        }
      }
    }
  }, [selectedGeofence, setFieldValue, values.geofence, values.area, t]);

  const handleLocationTypeSelect = (type: LocationType) => {
    setFieldValue("macroRegionId", undefined);
    setFieldValue("stationRegion", "");

    setFieldValue("geofence", []);
    setFieldValue("locationId", undefined);
    clearGeofence();
    clearSelectedStation?.();

    insightAlertStore.setAlertLocationType(type);

    // Auto-enter selection mode only for point type
    // For area type, show the MapDrawOptions component first
    if (type === "point") {
      handlePointSelectionMode?.();
    }
  };

  // Check if there's any location data that would be lost
  const hasLocationData = useCallback(() => {
    const hasStation = !!insightAlertStore.selectedStationForAlert;
    const hasGeofence =
      insightAlertStore.selectedGeofence &&
      insightAlertStore.selectedGeofence.length > 0;
    const hasFormGeofence = values.geofence && values.geofence.length > 0;
    const hasFormStation = !!values.stationRegion;
    return hasStation || hasGeofence || hasFormGeofence || hasFormStation;
  }, [
    insightAlertStore.selectedStationForAlert,
    insightAlertStore.selectedGeofence,
    values.geofence,
    values.stationRegion,
  ]);

  // Execute the back action (clear location data and return to type selection)
  const executeBackAction = useCallback(() => {
    clearSelectedStation?.();
    clearGeofence();
    setFieldValue("stationRegion", "");
    setFieldValue("macroRegionId", undefined);
    setFieldValue("geofence", []);
    setFieldValue("locationId", undefined);
    setFieldValue("area", undefined);
    insightAlertStore.setAlertLocationType(null);
    insightAlertStore.setAreaSelectorVisible(false);
  }, [clearSelectedStation, clearGeofence, setFieldValue, insightAlertStore]);

  // Execute back from geofence to MapDrawOptions (keeps area type, just clears geofence)
  const executeBackFromGeofence = useCallback(() => {
    clearGeofence();
    setFieldValue("geofence", []);
    setFieldValue("locationId", undefined);
    setFieldValue("area", undefined);
    insightAlertStore.setAreaSelectorVisible(false);
    // Keep alertLocationType as "area" to show MapDrawOptions
  }, [clearGeofence, setFieldValue, insightAlertStore]);

  // Handler for back from GeofenceMapContainer - goes back to MapDrawOptions
  const handleBackFromGeofence = useCallback(() => {
    // Check if there's geofence data that would be lost
    const hasGeofence =
      insightAlertStore.selectedGeofence &&
      insightAlertStore.selectedGeofence.length > 0;
    const hasFormGeofence = values.geofence && values.geofence.length > 0;
    const hasGeofenceData = hasGeofence || hasFormGeofence;

    if (hasGeofenceData) {
      setPendingBackAction(() => () => executeBackFromGeofence());
      setIsLocationWarningModalOpen(true);
    } else {
      executeBackFromGeofence();
    }
  }, [
    executeBackFromGeofence,
    insightAlertStore.selectedGeofence,
    values.geofence,
  ]);

  const handleBack = useCallback(() => {
    if (hasLocationData()) {
      setPendingBackAction(() => () => executeBackAction());
      setIsLocationWarningModalOpen(true);
    } else {
      insightAlertStore.setAlertLocationType(null);
      insightAlertStore.setAreaSelectorVisible(false);
    }
  }, [executeBackAction, hasLocationData, insightAlertStore]);

  const handleClearStation = useCallback(() => {
    if (hasLocationData()) {
      setPendingBackAction(() => () => executeBackAction());
      setIsLocationWarningModalOpen(true);
    } else {
      insightAlertStore.setAlertLocationType(null);
    }
  }, [executeBackAction, hasLocationData, insightAlertStore]);

  const handleLocationWarningConfirm = useCallback(() => {
    if (pendingBackAction) {
      pendingBackAction();
      setPendingBackAction(null);
    }
    setIsLocationWarningModalOpen(false);
  }, [pendingBackAction]);

  const handleLocationWarningCancel = useCallback(() => {
    setPendingBackAction(null);
    setIsLocationWarningModalOpen(false);
  }, []);

  // State for dropzone visibility in MapDrawOptions
  const [isDropZoneVisible, setIsDropZoneVisible] = useState(false);

  // Show LocationTypeSelector when no location type is selected,
  // or when point is selected but no station
  // Don't show when location is locked (prefilled from existing location)
  const shouldShowLocationTypeSelector =
    !isLocationLocked &&
    (!insightAlertStore.alertLocationType ||
      (insightAlertStore.alertLocationType === "point" &&
        !selectedStationForAlert));

  // Show MapDrawOptions when area is selected but no geofence yet AND area selector is not visible
  // Don't show when location is locked
  const shouldShowMapDrawOptions =
    !isLocationLocked &&
    insightAlertStore.alertLocationType === "area" &&
    (!selectedGeofence || selectedGeofence.length === 0) &&
    !areaSelectorVisible;

  // Show AreaSelector dropdown when area type is selected, no geofence yet, and areaSelectorVisible is true
  // Don't show when location is locked
  const shouldShowAreaSelector =
    !isLocationLocked &&
    insightAlertStore.alertLocationType === "area" &&
    (!selectedGeofence || selectedGeofence.length === 0) &&
    areaSelectorVisible;

  // Handler to go back from area selector to MapDrawOptions
  const handleBackFromAreaSelector = useCallback(() => {
    handleEnableAreaSelector?.(false);
  }, [handleEnableAreaSelector]);

  return (
    <Stack spacing={6}>
      {/* Location Content - No tabs, directly show location selector */}
      {shouldShowLocationTypeSelector && (
        <>
          <LocationTypeSelector onSelect={handleLocationTypeSelect} t={t} />
          {(errors.geofence || errors.stationRegion) &&
            (touched.geofence || touched.stationRegion) && (
              <Text color="red.500" fontSize="sm" mt={2}>
                {t("pleaseSelectPointOrArea")}
              </Text>
            )}
        </>
      )}

      {shouldShowMapDrawOptions && (
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <Flex gap="12px" mb="16px" justifyContent="space-between">
            <Flex gap="12px">
              <MapPinned color="#086AA9" size="20px" />
              <Text fontWeight={"400"} fontSize="md" color="#086AA9">
                {t("monitoredLocation")}
              </Text>
            </Flex>
            <BackButton onClick={handleBack} label={t("back") as string} />
          </Flex>
          <MapDrawOptions
            handleDrawingMode={handleDrawingMode}
            handleGeoJsonLoaded={handleGeoJsonLoaded}
            t={t}
            setIsGeofenceLoadedByGeoJson={setIsGeofenceLoadedByGeoJson}
            handleEnableAreaSelector={handleEnableAreaSelector}
            showSelectSavedAreaButton={areas.length > 0}
            isDropZoneVisible={isDropZoneVisible}
            setIsDropZoneVisible={setIsDropZoneVisible}
          />
        </Box>
      )}

      {/* Area Selector Dropdown - shown when user clicks "Select saved area" */}
      {shouldShowAreaSelector && (
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <Flex gap="12px" mb="16px" justifyContent="space-between">
            <Flex gap="12px">
              <MapPinned color="#086AA9" size="20px" />
              <Text fontWeight={"400"} fontSize="md" color="#086AA9">
                {t("monitoredLocation")}
              </Text>
            </Flex>
            <BackButton
              onClick={handleBackFromAreaSelector}
              label={t("back") as string}
            />
          </Flex>
          <FormControl isRequired>
            <FormLabel color="#414651" fontWeight={"500"}>
              {t("area")}
            </FormLabel>
            <Box>
              <Select
                id="select-area"
                showSearch
                optionFilterProp="children"
                placeholder={t("selectAreaPlaceholder") as string}
                onChange={(selectedAreaId: number) => {
                  if (handleAreaSelection) {
                    handleAreaSelection(selectedAreaId, areas, setFieldValue);
                  }
                }}
                value={values.locationId}
                style={{
                  width: "100%",
                  height: "40px",
                  borderColor: "#eff0f3",
                }}
                dropdownStyle={{ zIndex: 2000 }}
              >
                {areas.map((area) => (
                  <Select.Option key={area.id} value={area.id}>
                    {area.name}
                  </Select.Option>
                ))}
              </Select>
            </Box>
          </FormControl>
        </Box>
      )}

      {insightAlertStore.alertLocationType === "point" &&
        selectedStationForAlert && (
          <Box borderWidth="1px" borderRadius="md" p={4}>
            <Flex gap="12px" mb="16px" justifyContent="space-between">
              <Flex gap="12px">
                <MapPinned color="#086AA9" size="20px" />
                <Text fontWeight={"400"} fontSize="md" color="#086AA9">
                  {t("monitoredLocation")}
                </Text>
              </Flex>
              {!isLocationLocked && (
                <BackButton onClick={handleBack} label={t("back") as string} />
              )}
            </Flex>

            <FormControl
              isRequired
              isInvalid={!!errors.stationRegion && touched.stationRegion}
            >
              <MapPointPreview
                station={selectedStationForAlert}
                onEdit={isLocationLocked ? undefined : handlePointSelectionMode}
                onClear={isLocationLocked ? undefined : handleClearStation}
                hideEditButtons={isLocationLocked}
                t={t}
              />
              {errors.stationRegion && touched.stationRegion && (
                <FormErrorMessage mt={2}>
                  {errors.stationRegion}
                </FormErrorMessage>
              )}
            </FormControl>
          </Box>
        )}

      {insightAlertStore.alertLocationType === "area" &&
        selectedGeofence &&
        selectedGeofence.length > 0 && (
          <GeofenceMapContainer
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            selectedGeofence={selectedGeofence}
            handleDrawingMode={handleDrawingMode}
            handleGeoJsonLoaded={handleGeoJsonLoaded}
            t={t}
            showGeofenceError={showGeofenceError}
            clearGeofence={clearGeofence}
            setIsGeofenceLoadedByGeoJson={setIsGeofenceLoadedByGeoJson}
            areas={areas}
            handleAreaSelection={handleAreaSelection}
            handleEnableAreaSelector={handleEnableAreaSelector}
            areaSelectorVisible={areaSelectorVisible}
            hideSaveAreaButtonOnMap={areaSelectorVisible}
            onBackToTypeSelection={
              isLocationLocked ? undefined : handleBackFromGeofence
            }
            isLocationLocked={isLocationLocked}
          />
        )}

      <LocationDataWarningModal
        isOpen={isLocationWarningModalOpen}
        onClose={handleLocationWarningCancel}
        onConfirm={handleLocationWarningConfirm}
        t={t}
      />
    </Stack>
  );
};

export default observer(EventStep1BasicInfo);
